<script lang="ts">
	//Design system components
	import { ColorWheel, Container, Text, Toggle } from '@Shelly/shelly-design-system';
	//Svelte stores
	import { createEventDispatcher, getContext } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';

	export let disabled;

	const nightModeStore: SvelteStore<any> = getContext('nightMode');
	const dispatch = createEventDispatcher();

	const hasToggle =
		typeof $nightModeStore?.rgb !== 'undefined' && typeof $nightModeStore?.ct !== 'undefined';

	let loading = false;
	let rgb = $nightModeStore.rgb;
	let isEnabled = typeof rgb !== 'undefined' && rgb !== null;

	/**
	 * Validates and clamps a color channel value to be within 0-255 range
	 *
	 * @param _value - number|string The color channel value to validate
	 * @returns number The validated and clamped color channel value (0-255)
	 */
	const validate = (_value) => {
		const max = 255;
		const min = 0;

		if (+_value > max) {
			_value = max;
		}
		if (+_value < min) {
			_value = min;
		}
		return +_value;
	};

	/**
	 * Converts an RGB array [r, g, b] to an object with r, g, b properties
	 * Validates each channel value to ensure it's within 0-255 range
	 *
	 * @param color - number[] The RGB color array [r, g, b]
	 * @returns object An object with r, g, b properties
	 */
	const splitColors = (color) => {
		return {
			r: validate(color[0]),
			g: validate(color[1]),
			b: validate(color[2]),
		};
	};

	/**
	 * Converts a color object with r, g, b properties to an RGB array [r, g, b]
	 * Validates each channel value to ensure it's within 0-255 range
	 *
	 * @param color - object The color object with r, g, b properties
	 * @returns number[] An RGB color array [r, g, b]
	 */
	const joinColors = (color) => {
		return [validate(color.r), validate(color.g), validate(color.b)];
	};

	let color = isEnabled ? splitColors(rgb) : { r: 0, g: 0, b: 0 };

	/**
	 * Handles color value changes from the color wheel
	 * Updates the local state and dispatches the new RGB value to the parent component
	 *
	 * @param e - CustomEvent The event object containing the new color in e.detail.color
	 * @returns void
	 */
	const changeColor = (e) => {
		color = e.detail.color;
		dispatch('update', {
			rgb: joinColors(color),
		});
	};

	/**
	 * Handles the toggle enable/disable state for color
	 * When enabling, sets a default color (black) if rgb is null/undefined
	 * When disabling, sets rgb to null
	 *
	 * @returns void
	 */
	const handleToggle = () => {
		isEnabled = !isEnabled;
		if (isEnabled) {
			if (rgb === null || rgb === undefined) {
				const defaultColor = { r: 0, g: 0, b: 0 };
				color = defaultColor;
				dispatch('update', {
					rgb: joinColors(defaultColor),
				});
			} else {
				color = splitColors(rgb);
				dispatch('update', {
					rgb: joinColors(color),
				});
			}
		} else {
			dispatch('update', {
				rgb: null,
			});
		}
	};
</script>

{#if hasToggle}
	<Toggle id="toggle-6wb5u42" bind:checked={isEnabled} size="m" class="mb-s" on:click={handleToggle}>
		{$format('label_enable')}
	</Toggle>
{/if}

<Container hideSlotTop hideSlotBottom padding="0">
	<Text element="h2" class="mb-s"
		>{$format('select_type', {
			values: { type: $format('label_color') },
		})}</Text
	>

	<ColorWheel
		id="colorwheel-r3i6t9a"
		a11yProps={{
			saveButtonAriaLabel: $format('label_save_color', { default: 'Save color' }),
			colorInputBAriaLabel: $format('label_color_input_b', { default: 'Color input B' }),
			colorInputGAriaLabel: $format('label_color_input_g', { default: 'Color input G' }),
			colorInputRAriaLabel: $format('label_color_input_r', { default: 'Color input R' }),
		}}
		valuesType="input"
		{color}
		disabled={loading || !isEnabled || disabled}
		hideSaveBtn
		on:color_change={changeColor}
	/>
</Container>
